import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { CiamTokenResponse, CiamTokens } from './ciam.models';

const USER_KEY = 'auth-user';
const TOKEN_KEY = 'auth-token';
/**
 * Seule clé du `localStorage` : les jetons vivent dans le `sessionStorage` de chaque onglet,
 * qu'une déconnexion faite ailleurs ne peut pas atteindre. Cet horodatage, partagé par tous les
 * onglets, invalide les jetons obtenus avant lui.
 */
const LOGOUT_AT_KEY = 'ciam-logout-at';
const DEFAULT_ACCESS_TOKEN_LIFETIME_SECONDS = 90 * 60;
const CLOCK_SKEW_MS = 30 * 1000;

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly logged: BehaviorSubject<boolean>;
  private readonly loggedOutElsewhere = new Subject<void>();

  constructor() {
    this.logged = new BehaviorSubject<boolean>(this.hasValidAccessToken());
    // L'événement `storage` n'est émis que dans les *autres* onglets que celui qui écrit.
    window.addEventListener('storage', event => {
      if (event.key === LOGOUT_AT_KEY && event.newValue && this.logged.value) {
        this.clean();
        this.loggedOutElsewhere.next();
      }
    });
  }

  /**
   * À n'appeler que pour une déconnexion volontaire : les autres onglets ferment leur session
   * immédiatement, et un onglet dupliqué ensuite ne réutilise pas les jetons recopiés.
   */
  signalLogout(): void {
    window.localStorage.setItem(LOGOUT_AT_KEY, String(Date.now()));
    this.clean();
  }

  /** Émet quand un autre onglet s'est déconnecté et que la session de celui-ci vient d'être fermée. */
  getLoggedOutElsewhere(): Observable<void> {
    return this.loggedOutElsewhere.asObservable();
  }

  clean(): void {
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(USER_KEY);
    this.logged?.next(false);
  }

  saveTokens(response: CiamTokenResponse): CiamTokens {
    if (!response.accessToken || !response.refreshToken) {
      throw new Error('La réponse CIAM ne contient pas la paire de jetons attendue.');
    }

    const tokens: CiamTokens = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      tokenType: response.tokenType || 'Bearer',
      expiresAt: this.resolveExpiration(response),
      obtainedAt: Date.now(),
    };

    window.sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    this.logged.next(true);
    return tokens;
  }

  getTokens(): CiamTokens | undefined {
    const token = window.sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      return undefined;
    }

    try {
      return JSON.parse(token) as CiamTokens;
    } catch {
      this.clean();
      return undefined;
    }
  }

  getAccessToken(): string | undefined {
    return this.getTokens()?.accessToken;
  }

  getRefreshToken(): string | undefined {
    return this.getTokens()?.refreshToken;
  }

  saveUser(user: string): void {
    window.sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    this.logged.next(true);
  }

  getUser(): string {
    const user = window.sessionStorage.getItem(USER_KEY);
    if (user) {
      return JSON.parse(user) as string;
    }

    return '';
  }

  hasValidAccessToken(): boolean {
    const tokens = this.getTokens();
    return Boolean(tokens?.accessToken
      && Date.now() + CLOCK_SKEW_MS < tokens.expiresAt
      && this.obtainedAfterLastLogout(tokens));
  }

  private obtainedAfterLastLogout(tokens: CiamTokens): boolean {
    const lastLogoutAt = Number(window.localStorage.getItem(LOGOUT_AT_KEY));
    return !lastLogoutAt || (tokens.obtainedAt ?? 0) > lastLogoutAt;
  }

  isLoggedIn(): boolean {
    return this.hasValidAccessToken();
  }

  getLogged(): Observable<boolean> {
    return this.logged.asObservable();
  }

  private resolveExpiration(response: CiamTokenResponse): number {
    if (response.expiresIn && response.expiresIn > 0) {
      return Date.now() + response.expiresIn * 1000;
    }

    const jwtExpiration = this.readJwtExpiration(response.accessToken);
    return jwtExpiration ?? Date.now() + DEFAULT_ACCESS_TOKEN_LIFETIME_SECONDS * 1000;
  }

  private readJwtExpiration(accessToken: string): number | undefined {
    try {
      const payload = accessToken.split('.')[1];
      if (!payload) {
        return undefined;
      }

      const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(window.atob(normalizedPayload)) as { exp?: number };
      return claims.exp ? claims.exp * 1000 : undefined;
    } catch {
      return undefined;
    }
  }
}
